import React, { useState, useEffect } from 'react';
import { api } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar as CalendarIcon,
  Clock,
  User,
  Lock,
  Unlock,
  X,
  RefreshCw,
  Trash2
} from 'lucide-react';
import { formatTime12Hour } from '../../utils/timeFormat';

const BarberCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [openOverrides, setOpenOverrides] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [showAppointmentDialog, setShowAppointmentDialog] = useState(false);
  const [selectedAppointments, setSelectedAppointments] = useState([]);
  const [blockReason, setBlockReason] = useState('');
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringPattern, setRecurringPattern] = useState('weekly');
  const [recurringWeeks, setRecurringWeeks] = useState(4);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch appointments and blocked dates for current month
  useEffect(() => {
    fetchMonthData();
  }, [currentDate]);

  const fetchMonthData = async () => {
    setLoading(true);
    setError(null);
    try {
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const startDate = `${year}-${month}-01`;
      const endDate = `${year}-${month}-${getDaysInMonth(year, currentDate.getMonth())}`;

      // Fetch appointments for the month
      const aptsRes = await api.get(`/api/barber/appointments?start_date=${startDate}&end_date=${endDate}`);
      setAppointments(aptsRes.data || []);

      // Fetch blocked dates
      const blockedRes = await api.get('/api/barber/blocked-dates');
      setBlockedDates(blockedRes.data || []);

      // Fetch shop-wide holidays (federal + custom)
      try {
        const holRes = await api.get('/api/holidays/');
        setHolidays(holRes.data || []);
      } catch (_) {
        setHolidays([]);
      }

      // Fetch admin open-overrides for normally-closed Sun/Mon
      try {
        const ovRes = await api.get('/api/open-overrides/');
        setOpenOverrides(Array.isArray(ovRes.data) ? ovRes.data : []);
      } catch (_) {
        setOpenOverrides([]);
      }
    } catch (error) {
      console.error('Error fetching calendar data:', error);
      setError(error.message || 'Failed to load calendar');
      toast.error('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const cancelAppointment = async (appointmentId) => {
    if (!window.confirm('Are you sure you want to cancel this appointment?')) {
      return;
    }

    try {
      await api.delete(`/api/barber/appointments/${appointmentId}`);
      toast.success('Appointment cancelled successfully');
      
      // Refresh appointments
      fetchMonthData();
      
      // Update selected appointments if dialog is open
      const updatedApts = selectedAppointments.filter(apt => apt.id !== appointmentId);
      setSelectedAppointments(updatedApts);
      
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel appointment');
    }
  };

  const getFirstDayOfMonth = () => {
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    return date.getDay(); // 0 = Sunday, 1 = Monday, etc.
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleDateClick = (day) => {
    const clickedDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    const dateStr = clickedDate.toISOString().split('T')[0];
    setSelectedDate(dateStr);

    // Get appointments for this date
    const dayAppointments = appointments.filter(apt => apt.date === dateStr);
    
    if (dayAppointments.length > 0) {
      setSelectedAppointments(dayAppointments);
      setShowAppointmentDialog(true);
    } else {
      // No appointments, show block dialog
      setShowBlockDialog(true);
    }
  };

  const blockDate = async () => {
    if (!selectedDate) return;

    try {
      const blockData = {
        date: selectedDate,
        reason: blockReason || 'Unavailable',
        is_recurring: isRecurring,
        recurring_pattern: isRecurring ? recurringPattern : null,
        recurring_count: isRecurring ? recurringWeeks : null
      };
      
      await api.post('/api/barber/blocked-dates', blockData);
      
      const message = isRecurring 
        ? `Blocked ${recurringWeeks} ${recurringPattern} dates successfully`
        : 'Date blocked successfully';
      
      toast.success(message);
      setShowBlockDialog(false);
      setBlockReason('');
      setIsRecurring(false);
      setRecurringPattern('weekly');
      setRecurringWeeks(4);
      fetchMonthData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to block date');
    }
  };

  const unblockDate = async (blockId) => {
    try {
      await api.delete(`/api/barber/blocked-dates/${blockId}`);
      toast.success('Date unblocked');
      fetchMonthData();
    } catch (error) {
      toast.error('Failed to unblock date');
    }
  };

  const isDateBlocked = (dateStr) => {
    return blockedDates.some(block => block.date === dateStr);
  };

  const getAppointmentsForDate = (dateStr) => {
    return appointments.filter(apt => apt.date === dateStr);
  };

  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth();
    
    const days = [];
    const monthStart = new Date(year, month, 1);
    
    // Day headers
    const dayHeaders = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    // Empty cells before first day
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="p-2 h-24 bg-zinc-900/20"></div>);
    }
    
    // Calendar days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateStr = date.toISOString().split('T')[0];
      const dayOfWeek = date.getDay();
      const isToday = dateStr === new Date().toISOString().split('T')[0];
      const isBlocked = isDateBlocked(dateStr);
      const dayAppointments = getAppointmentsForDate(dateStr);
      const isSunday = dayOfWeek === 0;
      const isMonday = dayOfWeek === 1;
      const isWeekend = isSunday || isMonday;

      // Shop-wide holiday for this date (federal + custom from /api/holidays)
      const holiday = holidays.find((h) => h.date === dateStr && h.active !== false);
      const holidayClosed = holiday?.closed === true;
      const holidayLimited = holiday && !holiday.closed;
      // If it's a normally-closed Sun/Mon, treat it as OPEN if EITHER an
      // admin open-override exists OR a holiday marks it as limited/open.
      const openedByAdmin = openOverrides.some((o) => o.date === dateStr) || holidayLimited;
      const isClosedWeekend = isWeekend && !openedByAdmin;

      days.push(
        <div
          key={day}
          onClick={() => !isClosedWeekend && !holidayClosed && handleDateClick(day)}
          className={`
            p-2 h-24 border border-zinc-800 
            ${isClosedWeekend || holidayClosed ? 'bg-zinc-900/50 cursor-not-allowed' : 'cursor-pointer hover:bg-zinc-800/50'}
            ${isToday ? 'ring-2 ring-lime-400' : ''}
            ${holidayClosed ? 'bg-red-900/30' : holidayLimited ? 'bg-amber-900/20' : openedByAdmin ? 'bg-lime-900/20' : isBlocked ? 'bg-red-900/20' : ''}
            transition-all
          `}
          title={holiday ? `${holiday.name}${holidayClosed ? ' — Closed' : holiday.members_only ? ' — Members only' : ' — Limited hours'}` : (openedByAdmin ? 'Special Open (normally closed)' : undefined)}
        >
          <div className="flex justify-between items-start mb-1">
            <span className={`text-sm font-semibold ${isToday ? 'text-lime-400' : 'text-white'}`}>
              {day}
            </span>
            {isBlocked && !holiday && (
              <Lock className="w-3 h-3 text-red-400" />
            )}
            {isClosedWeekend && !holiday && (
              <Badge variant="outline" className="text-xs px-1 py-0 text-zinc-500 border-zinc-700">
                Closed
              </Badge>
            )}
            {isWeekend && openedByAdmin && !holiday && (
              <Badge variant="outline" className="text-[9px] px-1 py-0 leading-tight text-lime-300 border-lime-600/50">
                Open
              </Badge>
            )}
            {holiday && (
              <Badge
                variant="outline"
                className={`text-[9px] px-1 py-0 leading-tight whitespace-nowrap ${
                  holidayClosed ? 'text-red-300 border-red-700/50' : 'text-amber-300 border-amber-700/50'
                }`}
              >
                {holidayClosed ? 'Closed' : holiday.members_only ? 'Members' : 'Limited'}
              </Badge>
            )}
          </div>

          {/* Holiday name (truncated) — shown above appointments */}
          {holiday && (
            <div className={`text-[10px] font-semibold mb-1 truncate ${
              holidayClosed ? 'text-red-300' : 'text-amber-300'
            }`}>
              {holiday.name}
            </div>
          )}

          <div className="space-y-1 overflow-hidden">
            {dayAppointments.slice(0, 2).map((apt, idx) => (
              <div
                key={idx}
                className="text-xs bg-lime-500/20 text-lime-300 px-1 py-0.5 rounded truncate"
              >
                {formatTime12Hour(apt.time)} - {apt.customer_name}
              </div>
            ))}
            {dayAppointments.length > 2 && (
              <div className="text-xs text-zinc-400">
                +{dayAppointments.length - 2} more
              </div>
            )}
          </div>
        </div>
      );
    }
    
    return (
      <div className="grid grid-cols-7 gap-px bg-zinc-800">
        {/* Day headers */}
        {dayHeaders.map(day => (
          <div key={day} className="p-2 text-center font-semibold text-zinc-400 bg-zinc-900">
            {day}
          </div>
        ))}
        
        {/* Calendar days */}
        {days}
      </div>
    );
  };

  return (
    <div className="space-y-4 pb-20">
      {/* Error State */}
      {error && (
        <Card className="bg-red-900/20 border-red-500/50">
          <CardContent className="p-6 text-center">
            <div className="text-red-400 mb-2">⚠️ Error Loading Calendar</div>
            <p className="text-sm text-zinc-300 mb-4">{error}</p>
            <Button
              onClick={fetchMonthData}
              className="bg-red-600 hover:bg-red-700"
            >
              Retry
            </Button>
          </CardContent>
        </Card>
      )}
      
      {/* Calendar Header */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <CardTitle className="text-xl sm:text-2xl text-white flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 sm:w-6 sm:h-6 text-lime-400" />
              <span className="text-base sm:text-2xl">
                {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </span>
            </CardTitle>
            
            <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
              <Button
                onClick={goToToday}
                variant="outline"
                size="sm"
                className="border-zinc-700 text-white hover:bg-zinc-800 text-xs sm:text-sm"
              >
                Today
              </Button>
              
              <div className="flex items-center gap-1">
                <Button
                  onClick={previousMonth}
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-zinc-800"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                
                <Button
                  onClick={nextMonth}
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-zinc-800"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="overflow-x-auto">
          {loading ? (
            <div className="text-center py-12 text-zinc-400">Loading calendar...</div>
          ) : (
            <div className="min-w-[600px]">
              {renderCalendar()}
            </div>
          )}
          
          {/* Legend */}
          <div className="mt-4 flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm text-zinc-400">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 sm:w-4 sm:h-4 ring-2 ring-lime-400 rounded"></div>
              <span>Today</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 sm:w-4 sm:h-4 bg-lime-500/20 rounded"></div>
              <span>Appointments</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 sm:w-4 sm:h-4 bg-red-900/20 rounded flex items-center justify-center">
                <Lock className="w-2 h-2 text-red-400" />
              </div>
              <span>Blocked</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 sm:w-4 sm:h-4 bg-zinc-900/50 rounded"></div>
              <span>Closed</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Block Date Dialog */}
      <Dialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-red-400" />
              Block Date{isRecurring && 's'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-300">Starting Date</Label>
              <Input
                value={selectedDate || ''}
                disabled
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            
            <div>
              <Label className="text-zinc-300">Reason (Optional)</Label>
              <Textarea
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                placeholder="Vacation, Personal, Sick, etc."
                className="bg-zinc-800 border-zinc-700 text-white"
                rows={2}
              />
            </div>
            
            {/* Recurring Block Option */}
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 text-purple-400" />
                  <Label className="text-white font-semibold">Recurring Block</Label>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isRecurring}
                    onChange={(e) => setIsRecurring(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-zinc-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                </label>
              </div>
              
              {isRecurring && (
                <div className="space-y-3 animate-in fade-in slide-in-from-top-2">
                  <div>
                    <Label className="text-zinc-300 text-sm">Repeat Pattern</Label>
                    <select
                      value={recurringPattern}
                      onChange={(e) => setRecurringPattern(e.target.value)}
                      className="w-full mt-1 bg-zinc-800 border border-zinc-700 text-white rounded-md p-2"
                    >
                      <option value="weekly">Every Week (Same Day)</option>
                      <option value="biweekly">Every 2 Weeks</option>
                      <option value="monthly">Every Month (Same Date)</option>
                    </select>
                  </div>
                  
                  <div>
                    <Label className="text-zinc-300 text-sm">
                      Number of Occurrences: {recurringWeeks}
                    </Label>
                    <input
                      type="range"
                      min="2"
                      max="12"
                      value={recurringWeeks}
                      onChange={(e) => setRecurringWeeks(parseInt(e.target.value))}
                      className="w-full mt-1"
                    />
                    <div className="flex justify-between text-xs text-zinc-500 mt-1">
                      <span>2</span>
                      <span>12 times</span>
                    </div>
                  </div>
                  
                  <div className="bg-zinc-800 rounded p-2 text-xs text-zinc-400">
                    ℹ️ Will block {recurringWeeks} {recurringPattern === 'weekly' ? 'weeks' : recurringPattern === 'biweekly' ? 'bi-weekly periods' : 'months'} starting from {selectedDate}
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex gap-2">
              <Button
                onClick={blockDate}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                <Lock className="w-4 h-4 mr-2" />
                {isRecurring ? `Block ${recurringWeeks} Dates` : 'Block Date'}
              </Button>
              
              <Button
                onClick={() => {
                  setShowBlockDialog(false);
                  setIsRecurring(false);
                  setBlockReason('');
                }}
                variant="outline"
                className="border-zinc-700 text-white hover:bg-zinc-800"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Appointments Dialog */}
      <Dialog open={showAppointmentDialog} onOpenChange={setShowAppointmentDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
              <CalendarIcon className="w-4 h-4 sm:w-5 sm:h-5 text-lime-400" />
              Appointments for {selectedDate}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3 max-h-[50vh] overflow-y-auto">
            {selectedAppointments.map((apt, idx) => (
              <Card key={idx} className="bg-zinc-800 border-zinc-700">
                <CardContent className="p-3 sm:p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-2 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Clock className="w-4 h-4 text-yellow-400" />
                        <span className="font-semibold text-white text-sm sm:text-base">
                          {formatTime12Hour(apt.time)}
                        </span>
                        <Badge className="bg-lime-500/20 text-lime-300 text-xs">
                          {apt.status}
                        </Badge>
                      </div>
                      
                      <div className="flex items-center gap-2 text-zinc-300 text-sm">
                        <User className="w-4 h-4" />
                        <span>{apt.customer_name}</span>
                      </div>
                      
                      <div className="text-xs sm:text-sm text-zinc-400">
                        {apt.service_name} - ${apt.service_price}
                      </div>
                    </div>
                    
                    <Button
                      onClick={() => cancelAppointment(apt.id)}
                      variant="destructive"
                      size="sm"
                      className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/50 h-8 px-3"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {selectedAppointments.length === 0 && (
              <div className="text-center py-8 text-zinc-400 text-sm">
                No appointments for this date
              </div>
            )}
          </div>
          
          <Button
            onClick={() => setShowAppointmentDialog(false)}
            variant="outline"
            className="w-full sm:w-auto border-zinc-700 text-white hover:bg-zinc-800"
          >
            Close
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BarberCalendar;
